import { BaseService } from './core/BaseService';
import { apiClient } from './core/ApiClient';

class TicketService extends BaseService {
  constructor() {
    super('/tickets');
  }

  // Get all tickets with enhanced filtering
  async getTickets(params = {}) {
    return this.getPaginated(params);
  }

  // Get my assigned tickets
  async getMyTickets(params = {}) {
    return this.customEndpoint('my', 'GET', null, params);
  }

  // Get overdue tickets (Admin only)
  async getOverdueTickets(params = {}) {
    return this.customEndpoint('overdue', 'GET', null, params);
  }

  // Get ticket statistics
  async getTicketStats(params = {}) {
    return this.getStats(params);
  }

  // Get ticket by number
  async getTicketByNumber(ticketNumber) {
    if (!ticketNumber) throw new Error('Ticket number is required');
    return apiClient.get(`${this.baseEndpoint}/number/${ticketNumber}`);
  }

  // Get single ticket (enhanced)
  async getTicket(ticketId) {
    return this.getById(ticketId);
  }

  // Create new ticket
  async createTicket(ticketData) {
    this.validateTicketData(ticketData);
    return this.create(ticketData);
  }

  // Update ticket
  async updateTicket(ticketId, ticketData) {
    return this.update(ticketId, ticketData);
  }

  // Update ticket status
  async updateTicketStatus(ticketId, status, reason = '') {
    if (!ticketId) throw new Error('Ticket ID is required');
    if (!status) throw new Error('Status is required');
    
    return this.action(ticketId, 'status', { status, reason });
  }

  // Assign ticket
  async assignTicket(ticketId, assignedTo, reason = '') {
    if (!ticketId) throw new Error('Ticket ID is required');
    if (!assignedTo) throw new Error('Assigned user ID is required');
    
    return this.action(ticketId, 'assign', { 
      assigned_to: assignedTo, 
      reason 
    });
  }

  // Get customer tickets
  async getCustomerTickets(customerId, params = {}) {
    if (!customerId) throw new Error('Customer ID is required');
    return apiClient.get(`${this.baseEndpoint}/customer/${customerId}/tickets?${apiClient.buildQuery(params)}`);
  }

  // Get tickets by user
  async getTicketsByUser(userId, params = {}) {
    if (!userId) throw new Error('User ID is required');
    return apiClient.get(`${this.baseEndpoint}/by-user/${userId}?${apiClient.buildQuery(params)}`);
  }

  // Search tickets
  async searchTickets(query, filters = {}) {
    return this.search(query, filters);
  }

  // Get ticket analytics
  async getAnalytics(params = {}) {
    return this.customEndpoint('analytics', 'GET', null, params);
  }

  // Ticket Comments
  async getComments(ticketId, params = {}) {
    return this.getRelated(ticketId, 'comments', params);
  }

  async addComment(ticketId, commentData) {
    if (!ticketId) throw new Error('Ticket ID is required');
    if (!commentData?.content) throw new Error('Comment content is required');
    
    return apiClient.post('/ticket-comments', {
      ticket_id: ticketId,
      ...commentData
    });
  }

  async updateComment(commentId, commentData) {
    if (!commentId) throw new Error('Comment ID is required');
    return apiClient.put(`/ticket-comments/${commentId}`, commentData);
  }

  async deleteComment(commentId) {
    if (!commentId) throw new Error('Comment ID is required');
    return apiClient.delete(`/ticket-comments/${commentId}`);
  }

  // Ticket Attachments
  async getAttachments(ticketId) {
    return this.getRelated(ticketId, 'attachments');
  }

  async uploadAttachment(ticketId, file, metadata = {}) {
    if (!ticketId) throw new Error('Ticket ID is required');
    if (!file) throw new Error('File is required');
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata
    Object.entries(metadata).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    return apiClient.upload(`${this.baseEndpoint}/${ticketId}/attachments`, formData);
  }

  async deleteAttachment(ticketId, attachmentId) {
    if (!ticketId || !attachmentId) {
      throw new Error('Ticket ID and attachment ID are required');
    }
    
    return apiClient.delete(`${this.baseEndpoint}/${ticketId}/attachments/${attachmentId}`);
  }

  // Ticket History
  async getHistory(ticketId) {
    return this.getRelated(ticketId, 'history');
  }

  // Bulk operations
  async bulkUpdateTickets(ticketIds, updateData) {
    return this.bulkUpdate(ticketIds, updateData);
  }

  async bulkAssign(ticketIds, assignedTo, reason = '') {
    if (!Array.isArray(ticketIds) || ticketIds.length === 0) {
      throw new Error('Ticket IDs array is required');
    }
    if (!assignedTo) throw new Error('Assigned user ID is required');
    
    return apiClient.post(`${this.baseEndpoint}/bulk-assign`, {
      ticket_ids: ticketIds,
      assigned_to: assignedTo,
      reason
    });
  }

  async bulkStatusUpdate(ticketIds, status, reason = '') {
    if (!Array.isArray(ticketIds) || ticketIds.length === 0) {
      throw new Error('Ticket IDs array is required');
    }
    if (!status) throw new Error('Status is required');
    
    return apiClient.post(`${this.baseEndpoint}/bulk-status`, {
      ticket_ids: ticketIds,
      status,
      reason
    });
  }

  // Validation helpers
  validateTicketData(data) {
    const required = ['subject', 'body', 'priority', 'module_id', 'category_id'];
    const missing = required.filter(field => !data[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate priority
    const validPriorities = ['low', 'medium', 'high', 'critical'];
    if (!validPriorities.includes(data.priority)) {
      throw new Error('Invalid priority. Must be one of: ' + validPriorities.join(', '));
    }
  }

  // Status helpers
  async canUpdateStatus(ticketId, newStatus) {
    try {
      const ticket = await this.getById(ticketId);
      return this.isValidStatusTransition(ticket.status, newStatus);
    } catch (error) {
      console.error('Error checking status update permission:', error);
      return false;
    }
  }

  isValidStatusTransition(currentStatus, newStatus) {
    const transitions = {
      'not_started': ['in_progress', 'cancelled'],
      'in_progress': ['on_hold', 'completed', 'cancelled'],
      'on_hold': ['in_progress', 'cancelled'],
      'completed': ['reopened'],
      'reopened': ['in_progress', 'reclosed'],
      'reclosed': ['reopened'],
      'cancelled': [], // Cannot transition from cancelled
      'escalated': ['in_progress', 'completed']
    };
    
    return transitions[currentStatus]?.includes(newStatus) || false;
  }

  // Export tickets
  async exportTickets(params = {}, format = 'csv') {
    return this.export(params, format);
  }

  // Duplicate ticket
  async duplicateTicket(ticketId, modifications = {}) {
    if (!ticketId) throw new Error('Ticket ID is required');
    
    return this.action(ticketId, 'duplicate', modifications);
  }

  // Merge tickets
  async mergeTickets(primaryTicketId, secondaryTicketIds, reason = '') {
    if (!primaryTicketId) throw new Error('Primary ticket ID is required');
    if (!Array.isArray(secondaryTicketIds) || secondaryTicketIds.length === 0) {
      throw new Error('Secondary ticket IDs array is required');
    }
    
    return apiClient.post(`${this.baseEndpoint}/merge`, {
      primary_ticket_id: primaryTicketId,
      secondary_ticket_ids: secondaryTicketIds,
      reason
    });
  }

  // Escalate ticket
  async escalateTicket(ticketId, escalationData) {
    if (!ticketId) throw new Error('Ticket ID is required');
    
    return this.action(ticketId, 'escalate', escalationData);
  }
}

// Export singleton instance
export const ticketService = new TicketService();

// Export class for testing or custom instances
export { TicketService };